import { CSSProperties, ReactNode } from 'react';
import { Drawer } from 'vaul';
import Handle from 'src/common/components/Handle';
import { Fn } from 'common/types.ts';

type Props = {
  /** Whether the bottom sheet is open */
  isOpen: boolean;
  /** Callback when the bottom sheet should close */
  onClose: Fn;
  /** Callback when open state changes */
  onOpen?: Fn;
  /** Content to render inside the bottom sheet */
  children: ReactNode;
  /** Title for accessibility (screen readers) */
  title?: string;
  /** Whether to show the drag handle */
  showHandle?: boolean;
  /** Whether to show the overlay backdrop */
  showOverlay?: boolean;
  /** Custom overlay style */
  overlayStyle?: CSSProperties;
  /** Custom content container style */
  contentStyle?: CSSProperties;
  /** Custom class name for content container */
  contentClassName?: string;
  /** z-index for the overlay */
  overlayZIndex?: number;
  /** z-index for the content */
  contentZIndex?: number;
  /** Max width of the bottom sheet */
  maxWidth?: string;
  /** Border radius for top corners */
  borderRadius?: string;
  /** Data test id for the content container */
  'data-testid'?: string;
};

const SwipeableBottomSheet = ({
  isOpen,
  onClose,
  onOpen,
  children,
  title,
  showHandle = true,
  showOverlay = true,
  overlayStyle,
  contentStyle,
  contentClassName = 'bg-base',
  overlayZIndex,
  contentZIndex,
  maxWidth = '420px',
  borderRadius = '16px',
  'data-testid': testId,
}: Readonly<Props>) => {
  const handleOpenChange = (open: boolean) => {
    if (open) {
      onOpen?.();
    } else {
      onClose();
    }
  };

  return (
    <Drawer.Root open={isOpen} onOpenChange={handleOpenChange} modal={true} shouldScaleBackground={false}>
      <Drawer.Portal>
        {showOverlay && (
          <Drawer.Overlay
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              zIndex: overlayZIndex,
              ...overlayStyle,
            }}
          />
        )}
        <Drawer.Content
          data-testid={testId}
          className={contentClassName}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: contentZIndex,
            maxWidth,
            margin: '0 auto',
            borderRadius: `${borderRadius} ${borderRadius} 0 0`,
            outline: 'none',
            direction: 'rtl',
            ...contentStyle,
          }}
        >
          {title && (
            <Drawer.Title style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>{title}</Drawer.Title>
          )}
          {showHandle && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '8px 0' }}>
              <Handle onClick={onClose} />
            </div>
          )}
          {children}
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default SwipeableBottomSheet;
